/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { ICreateClientOpts } from './@types/client';
import { XHubChatClient } from './client';
import { XHubChatScheduler } from './scheduler/scheduler';
import { MemoryStore } from './store/memory';

export * from './@types/beacon';
export * from './@types/client';
export * from './@types/common';
export * from './@types/contact';
export * from './@types/event';
export * from './@types/event-timeline-set';
export * from './@types/events';
export * from './@types/extensible_events';
export * from './@types/location';
export * from './@types/membership';
export * from './@types/partial';
export * from './@types/read_receipts';
export * from './@types/relations';
export * from './@types/request';
export * from './@types/room';
export * from './@types/room-member';
export * from './@types/user';

export * from './client';
export * from './emit/typed-event-emitter';

export * from './http';
export * from './models/beacon/beacon';
export * from './models/event/compare-event-ordering';
export * from './models/event/event';
export * from './models/event/event-timeline';
export * from './models/event/event-timeline-set';
export * from './models/event/timeline-window';
export * from './models/read-receipt';
export * from './models/realtime/interface';
export * from './models/relations/relations';
export * from './models/relations/relations-container';
export * from './models/room/room';
export * from './models/room/room-member';

export * from './models/room/room-receipts';
export * from './models/room/room-state';

export * from './models/room/room-summary';
export * from './models/thread/thread';

export * from './models/user/contact';
export * from './models/user/contacts-manager';
export { ContactsEvent, ContactsManager } from './models/user/contacts-manager';

export * from './models/user/user';

export { IndexedDBStore } from './store/indexeddb';

export * from './store/indexeddb-store-worker';
export { MemoryStore } from './store/memory';
export * from './sync/index';
export * from './sync/interface';

export * from './utils';
export * from './utils/logger';

function amendClientOpts(opts: ICreateClientOpts): ICreateClientOpts {
  opts.store
    = opts.store
      ?? new MemoryStore({
        localStorage: globalThis.localStorage,
      });
  opts.scheduler = opts.scheduler ?? new XHubChatScheduler();

  return opts;
}

/**
 * Construct a XHubChat Client. Similar to {@link XHubChatClient}
 * except that the 'request', 'store' and 'scheduler' dependencies are satisfied.
 * @param opts - The configuration options for this client. These configuration
 * options will be passed directly to {@link XHubChatClient}.
 *
 * @returns A new XHubChat client.
 * @see {@link XHubChatClient} for the full list of options for
 * `opts`.
 */
export function createClient(opts: ICreateClientOpts): XHubChatClient {
  return new XHubChatClient(amendClientOpts(opts));
}
